export type EventBarField = 'what' | 'where' | 'when' | 'who';

const EVENT_BAR_FIELDS: readonly EventBarField[] = ['what', 'where', 'when', 'who'];

const isEventBarField = (value: string | undefined): value is EventBarField =>
  EVENT_BAR_FIELDS.includes(value as EventBarField);

// Two fields are adjacent when they sit side by side in the bar (a single divider between them).
// EventBarV2 holds an adjacent pair visually merged while the traveling pill slides between them.
export const areAdjacentFields = (a: EventBarField, b: EventBarField): boolean =>
  Math.abs(EVENT_BAR_FIELDS.indexOf(a) - EVENT_BAR_FIELDS.indexOf(b)) === 1;

// Maps a focus/blur target to the event-bar field it belongs to by walking up to the nearest
// cell tagged with `data-event-bar-field`. Returns null for targets outside any field (e.g. the
// search button) or non-element targets. Drives the traveling focus pill in EventBarV2.
export const fieldFromEventTarget = (target: EventTarget | null): EventBarField | null => {
  if (!(target instanceof HTMLElement)) return null;
  const field = target.closest<HTMLElement>('[data-event-bar-field]')?.dataset.eventBarField;
  return isEventBarField(field) ? field : null;
};

const TABBABLE_SELECTOR =
  'a[href], area[href], button, details, input, select, textarea, [contenteditable="true"], [tabindex]';

// Base UI's popover hands focus off to an invisible guard rendered beside `Popover.Trigger` when
// the user tabs out of an open popup. The event bar fields anchor their popups to a label instead
// of a trigger — they are `aria-activedescendant` comboboxes, so focus has to stay on the input —
// which leaves that guard ref empty and dead-ends Tab inside the portal, dropping the user out of
// the page entirely. Move focus ourselves so an open field still tabs on to the next one.
//
// Returns whether focus actually moved: there may be no adjacent tabbable (Shift+Tab from the first
// one on the page, say), and a caller that has already suppressed the keystroke would then leave
// focus stranded on a field whose panel it just closed. Callers use this to fall back to the
// browser's native Tab instead.
export const focusAdjacentTabbable = (from: HTMLElement, direction: 1 | -1): boolean => {
  const candidates = Array.from(
    from.ownerDocument.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR),
  ).filter(
    element =>
      // Skip Base UI's own focus guards; everything else keeps its natural document order, so the
      // portaled popups stay reachable at the end of the page just as they were pre-Base UI.
      !element.hasAttribute('data-base-ui-focus-guard') &&
      element.tabIndex >= 0 &&
      !element.hasAttribute('disabled') &&
      // `offsetParent` is null for anything inside a `display: none` subtree — the compressed bar
      // is mounted but hidden below the `appbar-xl` breakpoint, and Tab has to skip it.
      element.offsetParent !== null,
  );

  const fromIndex = candidates.indexOf(from);
  if (fromIndex === -1) return false;

  const next = candidates[fromIndex + direction];
  if (!next) return false;

  next.focus();
  return true;
};
